// App.js
import React, { useState, useEffect } from 'react'; // Removed useMemo if not needed here
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

// Import Components
import PortfolioTables from './PortfolioTables';
import UserProfileMenu from './UserProfileMenu';
import DropdownMenu from './DropdownMenu'; // Use the generic menu component
import { UserGuideContent, Investment101Content, DeepValue101Content } from './LearningContent'; // Import learning content components

// Import Styles and Assets
import './Spinner.css';
import logo from './assets/images/dvlogo2.png';
import './App.css';
import './Menu.css';

// --- Define Menu Items ---
const mainMenuItems = [
  // Key 'portfolio' will show the PortfolioTables component
  { key: 'portfolio', label: 'Portfolio Analyzer' },
  { key: 'userguide', label: 'User Guide' },
  { key: 'invest101', label: 'Investing 101' },
  { key: 'deepvalue101', label: 'DeepValue Guide' },
];

// --- Placeholder Learning Content (if not imported) ---
// const Investment101Content = () => <div>Investment 101 Placeholder</div>;

function App() {
    // --- State ---
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    // State to control the main content view
    const [viewMode, setViewMode] = useState('loading'); // 'loading', 'login', 'portfolio', 'learning'
    const [learningTopicKey, setLearningTopicKey] = useState(null); // Stores key like 'invest101' when viewMode is 'learning'

    // --- Authentication and Profile Fetching ---
    useEffect(() => {
        const checkAuthAndFetchUser = async () => {
            setLoading(true);
            let authenticated = false;
            let profile = null;
            let nextView = 'login'; // Default to login view

            try {
                const authResponse = await fetch('https://api.ranzhao.com/auth/check', { credentials: 'include' });
                if (authResponse.ok) {
                    authenticated = true;
                    // Fetch profile only if authenticated
                    try {
                        const profileResponse = await fetch('https://api.ranzhao.com/user/profile', { credentials: 'include' });
                        if (profileResponse.ok) {
                            profile = await profileResponse.json();
                            nextView = 'portfolio'; // <-- Set default view to portfolio on successful login/profile fetch
                        } else { // Profile fetch failed
                            console.error('Failed to fetch profile, logging out.');
                            authenticated = false; // Treat as not authenticated
                        }
                    } catch (profileError) { // Network error fetching profile
                         console.error('Error fetching profile:', profileError);
                         authenticated = false; // Treat as not authenticated
                    }
                }
            } catch (error) { // Network error checking auth
                console.error('Error checking authentication:', error);
                 authenticated = false;
            } finally {
                setIsAuthenticated(authenticated);
                setUserProfile(profile);
                setViewMode(authenticated ? nextView : 'login'); // Set view based on final auth state
                setLoading(false);
            }
        };
        checkAuthAndFetchUser();
    }, []);

    // --- Navigation Handler ---
    // This function will be called when a menu item is clicked
    const handleNavigate = (key) => {
        if (key === 'portfolio') {
            setViewMode('portfolio');
            setLearningTopicKey(null);
        } else {
            // Assume it's a learning topic key
            setViewMode('learning');
            setLearningTopicKey(key);
        }
    };

    // --- Login/Logout Handlers ---
    const onSuccess = () => {
         setLoading(true);
         const redirectURL = encodeURIComponent(window.location.origin + '/');
         window.location.href = `https://auth.ranzhao.com/auth/google/login?redirect=${redirectURL}`;
    };

    const onFailure = () => {
         console.error('Login Failed');
         setLoading(false);
    };

    const handleLogout = () => {
         setLoading(true);
         fetch('https://api.ranzhao.com/auth/logout', { method: 'POST', credentials: 'include' })
             .finally(() => { // Always update frontend state on logout attempt
                 setIsAuthenticated(false);
                 setUserProfile(null);
                 setViewMode('login'); // <-- Go back to login view
                 setLoading(false);
             });
    };


    // --- Render Logic ---

    // Helper to render the main content based on viewMode
    const renderMainContent = () => {
        // Don't render main content if still doing initial load
        if (loading) {
             // Optionally show a larger spinner here, or nothing until loaded
             // return <div className="spinner main-spinner" aria-label="Loading..."></div>;
             return null;
        }

        switch (viewMode) {
            case 'portfolio':
                // Render the portfolio component if logged in
                return isAuthenticated ? <PortfolioTables /> : null; // Should be caught by 'login' mode, but safe check
            case 'learning':
                // Render specific learning content based on key
                switch (learningTopicKey) {
                    case 'invest101':
                        return <Investment101Content />;
                     case 'userguide': // Add this later
                        return <UserGuideContent />;
		     case 'deepvalue101': // Add this later
                        return <DeepValue101Content />;
                    default:
                        // Fallback if learning mode active but no specific topic
                        return <p>Select a learning topic from the menu.</p>;
                }
            case 'login': // Render login prompt/button
            default:
                return (
                    <div className="login-prompt-area">
                         <p>Please log in to continue:</p>
                         <GoogleLogin
                             onSuccess={onSuccess}
                             onError={onFailure}
                             id="google-login-button-main"
                         />
                    </div>
                );
        }
    };

    return (
        <GoogleOAuthProvider clientId="976824313987-63hftrnp4r07cidjk8ck8bbpm9tgc0ud.apps.googleusercontent.com">
            <div className="App">
                <header className="app-header">
                    <div className="app-title">
                        <img src={logo} alt="DeepValue DV logo" className="header-logo" />
                        {/* Render Menu dropdown ONLY when authenticated */}
                        {isAuthenticated && (
                            <DropdownMenu
                                triggerText="Menu"
                                items={mainMenuItems} // Pass the items array
                                onItemSelect={handleNavigate} // Pass the navigation handler
                                buttonClassName="nav-button menu-button" // Style the trigger button
                                containerClassName="dropdown-container header-dropdown" // Style the container
                            />
                        )}
                    </div>
                    <div className="auth-section">
                        {/* Profile menu OR null when logged out */}
                        {!loading && isAuthenticated && userProfile ? (
                            <UserProfileMenu userProfile={userProfile} onLogout={handleLogout} />
                        ) : (
                            null
                        )}
                    </div>
                </header>

                <main className="app-content">
                    {renderMainContent()}
                </main>
            </div>
        </GoogleOAuthProvider>
    );
}

export default App;
